import { create } from "zustand";
import { vehiclesApi } from "@services/api/vehicles";
import type {
  Vehicle,
  VehicleReservation,
  VehicleCategory,
  VehicleUsage,
} from "../types/garage/vehicules";

// --- Store ---

interface VehiculesStore {
  vehicles: Vehicle[];
  reservations: VehicleReservation[];
  isLoading: boolean;
  isRefreshing: boolean;
  selectedDate: string;
  viewMode: "reservations" | "parc";
  timeView: "semaine" | "jour";
  searchQuery: string;
  activeCategories: VehicleCategory[];
  activeUsages: VehicleUsage[];
  parcFuelTab: string;
  highlightedReservationId: string | null;
  _fetchedOrgId: string | undefined;

  fetchVehicles: (orgId: string) => Promise<void>;
  fetchReservations: (orgId: string) => Promise<void>;
  setSelectedDate: (d: string) => void;
  setHighlightedReservation: (id: string | null) => void;
  setViewMode: (m: "reservations" | "parc") => void;
  setTimeView: (t: "semaine" | "jour") => void;
  setSearchQuery: (q: string) => void;
  toggleCategory: (c: VehicleCategory) => void;
  toggleUsage: (u: VehicleUsage) => void;
  setParcFuelTab: (tab: string) => void;
  createVehicle: (vehicle: Omit<Vehicle, 'id'>) => Promise<Vehicle>;
  updateVehicle: (id: string, partial: Partial<Vehicle>) => void;
  updateReservation: (id: string, partial: Partial<VehicleReservation>) => void;
  getFilteredVehicles: () => Vehicle[];
  getWeekDays: () => { date: string; label: string; isWeekend: boolean }[];
  getReservationsForVehicle: (vehicleId: string) => VehicleReservation[];
  getReservationsForDate: (date: string) => VehicleReservation[];
}

const DAY_NAMES = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export const useVehiculesStore = create<VehiculesStore>((set, get) => ({
  vehicles: [],
  reservations: [],
  isLoading: false,
  isRefreshing: false,
  selectedDate: todayIso(),
  viewMode: "reservations",
  timeView: "semaine",
  searchQuery: "",
  activeCategories: ["autre", "mecanique"],
  activeUsages: ["pret"],
  parcFuelTab: "tous",
  highlightedReservationId: null,
  _fetchedOrgId: undefined,

  fetchVehicles: async (orgId: string) => {
    const state = get();
    if (state.isLoading || state.isRefreshing) return;
    const showLoading = state.vehicles.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, _fetchedOrgId: orgId });
    try {
      const vehicles = await vehiclesApi.list({ org_id: orgId });
      set({ vehicles, isLoading: false, isRefreshing: false });
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      set({ vehicles: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined });
    }
  },

  fetchReservations: async (orgId: string) => {
    try {
      const reservations = await vehiclesApi.listReservations({ org_id: orgId });
      set({ reservations });
    } catch (error) {
      console.error("Error fetching reservations:", error);
      set({ reservations: [] });
    }
  },

  setSelectedDate: (d) => set({ selectedDate: d }),
  setViewMode: (m) => set({ viewMode: m }),
  setTimeView: (t) => set({ timeView: t }),
  setSearchQuery: (q) => set({ searchQuery: q }),
  setParcFuelTab: (tab) => set({ parcFuelTab: tab }),
  setHighlightedReservation: (id) => set({ highlightedReservationId: id }),

  createVehicle: async (vehicle) => {
    const created = await vehiclesApi.create(vehicle);
    set({ vehicles: [...get().vehicles, created] });
    return created;
  },

  updateVehicle: (id, partial) => {
    set({
      vehicles: get().vehicles.map((v) =>
        v.id === id ? { ...v, ...partial } : v,
      ),
    });
    vehiclesApi.update(id, partial).catch((e) => console.error("Error updating vehicle:", e));
  },

  toggleCategory: (c) => {
    const cats = get().activeCategories;
    if (cats.includes(c)) {
      set({ activeCategories: cats.filter((x) => x !== c) });
    } else {
      set({ activeCategories: [...cats, c] });
    }
  },

  toggleUsage: (u) => {
    const usages = get().activeUsages;
    if (usages.includes(u)) {
      set({ activeUsages: usages.filter((x) => x !== u) });
    } else {
      set({ activeUsages: [...usages, u] });
    }
  },

  updateReservation: (id, partial) => {
    set({
      reservations: get().reservations.map((r) =>
        r.id === id ? { ...r, ...partial } : r,
      ),
    });
    vehiclesApi.updateReservation(id, partial).catch((e) => console.error("Error updating reservation:", e));
  },

  getFilteredVehicles: () => {
    const { vehicles, activeCategories, activeUsages, searchQuery } = get();
    return vehicles.filter((v) => {
      if (!activeCategories.includes(v.category) && !activeUsages.includes(v.usage)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !v.name.toLowerCase().includes(q) &&
          !v.plate.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  },

  getWeekDays: () => {
    const sel = new Date(get().selectedDate);
    const days: { date: string; label: string; isWeekend: boolean }[] = [];
    for (let i = -3; i <= 3; i++) {
      const d = new Date(sel.getFullYear(), sel.getMonth(), sel.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const dayName = DAY_NAMES[d.getDay()];
      const label = `${dayName} ${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
      days.push({ date: iso, label, isWeekend });
    }
    return days;
  },

  getReservationsForVehicle: (vehicleId) => {
    return get().reservations.filter((r) => r.vehicleId === vehicleId);
  },

  getReservationsForDate: (date) => {
    return get().reservations.filter(
      (r) => r.startDate <= date && r.endDate >= date,
    );
  },
}));
